import React from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Header from '@amzn/awsui-components-core/header';
import Multiselect from '@amzn/awsui-components-core/multiselect';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import StatusIndicator from '@amzn/awsui-components-core/status-indicator';
import Table from '@amzn/awsui-components-core/table';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { DatasetWizardState } from 'src/components/star-grid/datasets/hooks/useDatasetWizard';
import { monthSpan, type ScenarioRow } from 'src/components/star-grid/datasets/utils/scenarioRows';
import { SuggestionBadge } from 'src/components/star-grid/datasets/components/SuggestionBadge';

/** Which reporting scenarios read each of the dataset's scenario values, and the months each value covers. */
export const WizardScenariosStep: React.FC<{ state: DatasetWizardState; text: StarGridText }> = ({ state, text }) => {
  const options = state.reportingScenarios.map((name) => ({ value: name, label: name }));

  if (!state.scenarioColumn) {
    return (
      <Alert type="info" data-testid="wizard-scenarios-none">
        {text('wizardScenariosNoColumn')}
      </Alert>
    );
  }

  return (
    <SpaceBetween size="m">
      {state.reportingScenariosError && (
        <Alert type="error" action={<Button onClick={state.reloadReportingScenarios}>{text('retry')}</Button>}>
          {state.reportingScenariosError}
        </Alert>
      )}
      {state.scenarioSuggestionsError && <StatusIndicator type="warning">{state.scenarioSuggestionsError}</StatusIndicator>}
      <Table<ScenarioRow>
        data-testid="wizard-scenarios"
        variant="embedded"
        loading={state.scenarioSuggestionsLoading}
        loadingText={text('wizardScenarioValuesLoading')}
        items={state.scenarioRows}
        trackBy="read"
        header={
          <Header variant="h3" description={text('wizardScenariosHelp')}>
            {state.scenarioPeriod
              ? `${text('wizardScenariosPeriod')}: ${monthSpan(state.scenarioPeriod[0], state.scenarioPeriod[1])}`
              : text('wizardStepScenarios')}
          </Header>
        }
        empty={<Box textAlign="center">{text('wizardScenariosEmpty')}</Box>}
        columnDefinitions={[
          {
            id: 'value',
            header: text('wizardScenariosValue'),
            cell: (row) => (row.values.length > 1 ? `${row.read} (${row.values.join(', ')})` : row.read)
          },
          {
            id: 'months',
            header: text('wizardScenariosMonths'),
            cell: (row) => (row.first !== null && row.last !== null ? monthSpan(row.first, row.last) : '—')
          },
          {
            id: 'reporting',
            header: text('wizardScenariosReporting'),
            minWidth: 260,
            cell: (row) => (
              <Multiselect
                expandToViewport
                filteringType="auto"
                placeholder={text('wizardFeaturesUnmapped')}
                ariaLabel={`${text('wizardScenariosReporting')} ${row.read}`}
                options={options}
                selectedOptions={options.filter((option) => row.reporting.includes(option.value))}
                onChange={({ detail }) =>
                  state.mapScenarioRow(
                    row,
                    detail.selectedOptions.map((option) => option.value ?? '')
                  )
                }
              />
            )
          },
          {
            id: 'suggested',
            header: '',
            cell: (row) =>
              row.suggested.some((suggestion) => row.reporting.includes(suggestion.reporting)) ? (
                <SuggestionBadge text={text} suggestion={row.suggested.map((suggestion) => suggestion.reason).join('; ')} />
              ) : null
          }
        ]}
      />
    </SpaceBetween>
  );
};
